<template>
  <div class="inline-flex flex-nowrap items-center gap-2 w-full overflow-y-hidden overflow-x-auto scrollbar-hide pr-4">
    <UBadge label="Filters" color="gray" size="lg" class="w-28 cursor-pointer" @click="isFilterBoxOpened = true">
      <template #leading>
        <div class="flex justify-center items-center bg-red-500 text-white text-xs rounded-full w-4 h-4 flex-shrink-0" v-if="filtersCount">
          <span>{{ filtersCount }}</span>
        </div>
        <UIcon name="i-ph-funnel" class="w-4 h-4" v-else/>
      </template>
      <template #trailing>
        <UIcon name="i-heroicons-chevron-down" class="w-4 h-4" />
      </template>
    </UBadge>
    <UBadge color="gray" size="lg" class="text-nowrap cursor-pointer" :label="item.label" :class="{ 'ring-2 ring-primeval-500 bg-primeval-50': item.selected() }" @click="item.click" v-for="(item, index) in chips" :key="index">
      <template #leading>
        <UIcon :name="item.icon" class="w-4 h-4"/>
      </template>
    </UBadge>
  </div>
  <V4FilterBoxComponent side="bottom" :items="filterBoxItems" :preset="{...providerFilters, ...catalogFilters}" @apply="mergeFilters" @close="closeFilterBox" v-model="isFilterBoxOpened"/>
</template>
<script setup>
import { GoogleAnalytics } from '~/google_analytics/events';
import { EventParameter } from '~/google_analytics/events.enum';

const props = defineProps({
  config: { type: Object, required: true },
  data: { type: Object, required: true },
  preset: { type: Object },
  dcount: { type: Number, default: 0 }
});

const { gtag } = useGtag();
const auth = useAuth();
  const isAuthenticated = auth && auth.access_token;
  const userData = isAuthenticated ? {
    user_name: auth.user.first_name,
    user_phone: auth.user.phone,
    user_email: auth.user.email,
  } : {
    user_name: null,
    user_phone: null,
    user_email: null
  };
  
const emit = defineEmits(['apply']);
const isFilterBoxOpened = ref(false);

// --- Integrate popupBackHandler ---

// isPopupOpen will now control the V4FilterBoxComponent's visibility

const { isPopupOpen, openPopup, closePopup } = popupBackHandler();


const sortByDistance = 'distance:asc';
const sortByName = 'name:asc';

const { preset } = props;
const {
  categories = [], sub_categories = [], seller_np_ids = [], min_catalog_rating = 0, 
  is_open_now = false, is_deliverable = false, include_catalog = false, food_type = false, 
  sort = sortByDistance
} = preset;
const {
  price = [], is_stock_available = false, is_cancellable = false, is_returnable = false, 
  food_type: catalogFoodType = food_type, min_discount_percentage = false, sort: catalogSort = sortByName, ...rest 
} = preset.catalog_filters || {};

const providerFilters = ref({
  categories,
  sub_categories: sub_categories.map((k) => k.toLowerCase()),
  seller_np_ids,
  min_catalog_rating,
  is_open_now,
  is_deliverable,
  food_type,
  include_catalog,
  sort
});

const catalogFilters = ref({
  price,
  is_stock_available,
  is_cancellable,
  is_returnable,
  min_discount_percentage,
  food_type: catalogFoodType,
  sort: catalogSort,
  ...rest
});

const filtersCount = computed(() => {
  let total = useFilterSanitizer(providerFilters.value).count;
  if (providerFilters.value.include_catalog) {
    total += useFilterSanitizer(catalogFilters.value).count;
  }
  return total - Number(props.dcount);
});

const openNowChip = {
  id: 'filter_open_now',
  label: 'Open Now',
  icon: 'i-ph-check',
  selected: () => providerFilters.value.is_open_now,
  click: () => {
    providerFilters.value.is_open_now = !providerFilters.value.is_open_now;
    applyFilters();
  }
};
const deliverableChip = {
  id: 'filter_is_deliverable',
  label: 'Deliverable',
  icon: 'i-ph-check',
  selected: () => providerFilters.value.is_deliverable,
  click: () => {
    providerFilters.value.is_deliverable = !providerFilters.value.is_deliverable;
    applyFilters();
  }
};
const topRatedChip = {
  id: 'sort_top_rated',
  label: 'Top Rated',
  icon: 'i-ph-sort-descending',
  selected: () => providerFilters.value.sort === 'assorted:desc',
  click: () => {
    providerFilters.value.sort = topRatedChip.selected() ? 'distance:asc' : 'assorted:desc';
    applyFilters();
  }
};
const nearestChip = {
  id: 'sort_nearest',
  label: 'Nearest',
  icon: 'i-ph-sort-descending',
  selected: () => providerFilters.value.sort === 'distance:asc',
  click: () => {
    providerFilters.value.sort = 'distance:asc';
    applyFilters();
  }
};
const vegOnlyChip = {
  id: 'filter_veg_only',
  label: 'Veg only',
  icon: 'i-iconoir-vegan',
  selected: () => providerFilters.value.food_type === 'veg',
  click: () => {
    providerFilters.value.food_type = providerFilters.value.food_type === 'veg' ? false : 'veg';
    applyFilters();
  }
};
const fastestChip = {
  id: 'sort_fastest',
  label: 'Fast delivery',
  icon: 'i-ph-sort-descending',
  selected: () => providerFilters.value.sort === 'max_dispatch_time:asc',
  click: () => {
    providerFilters.value.sort = 'max_dispatch_time:asc';
    applyFilters();
  }
};
const stockAvailableChip = {
  id: 'filter_is_stock_available',
  label: 'Stock Available',
  icon: 'i-ph-check',
  selected: () => catalogFilters.value.is_stock_available,
  click: () => {
    catalogFilters.value.is_stock_available = !catalogFilters.value.is_stock_available;
    applyFilters();
  }
};
const cancellableChip = {
  id: 'filter_is_cancellable',
  label: 'Cancellable',
  icon: 'i-ph-check',
  selected: () => catalogFilters.value.is_cancellable,
  click: () => {
    catalogFilters.value.is_cancellable = !catalogFilters.value.is_cancellable;
    applyFilters();
  }
};
const returnableChip = {
  id: 'filter_is_returnable',
  label: 'Returnable',
  icon: 'i-ph-check',
  selected: () => catalogFilters.value.is_returnable,
  click: () => {
    catalogFilters.value.is_returnable = !catalogFilters.value.is_returnable;
    applyFilters();
  }
};
const betterDiscountChip = {
  id: 'sort_better_discount',
  label: 'Relevance',
  icon: 'i-ph-sort-ascending',
  selected: () => catalogFilters.value.sort === 'discount:desc',
  click: () => {
    catalogFilters.value.sort = 'discount:desc';
    applyFilters();
  }
};
const priceAscendingChip = {
  id: 'sort_price_ascending',
  label: 'Price: Low to High',
  icon: 'i-ph-sort-descending',
  selected: () => catalogFilters.value.sort === 'price:asc',
  click: () => {
    catalogFilters.value.sort = 'price:asc';
    applyFilters()
  }
};
const priceDescendingChip = {
  id: 'sort_price_descending',
  label: 'Price: High to Low',
  icon: 'i-ph-sort-ascending',
  selected: () => catalogFilters.value.sort === 'price:desc',
  click: () => {
    catalogFilters.value.sort = 'price:desc';
    applyFilters();
  }
};
const foodTypeChip = {
  id: 'filter_food_type',
  label: 'Veg only',
  icon: 'i-iconoir-vegan',
  selected: () => catalogFilters.value.food_type === 'veg',
  click: () => {
    catalogFilters.value.food_type = catalogFilters.value.food_type === 'veg' ? false : 'veg';
    applyFilters();
  }
};

const chips = computed(() => {
  const result = [];
  if (props.config.filter_open_now) result.push(openNowChip)
  if (props.config.filter_is_deliverable) result.push(deliverableChip)
  if (props.config.sort_top_rated) result.push(topRatedChip)
  if (props.config.filter_veg_only) result.push(vegOnlyChip)
  if (props.config.sort_nearest) result.push(nearestChip)
  if (props.config.sort_fastest) result.push(fastestChip)
  if (props.config.filter_food_type) result.push(foodTypeChip)
  if (props.config.sort_better_discount) result.push(betterDiscountChip)
  if (props.config.sort_price_ascending) result.push(priceAscendingChip)
  if (props.config.sort_price_descending) result.push(priceDescendingChip)
  if (props.config.filter_is_stock_available) result.push(stockAvailableChip)
  if (props.config.filter_is_cancellable) result.push(cancellableChip)
  if (props.config.filter_is_returnable) result.push(returnableChip)
  return result.sort(
    (a, b) => Boolean(b.selected()) - Boolean(a.selected())
  )
});

const filterItems = ref([]);
if (props.config.filter_sort_catalog) {
  filterItems.value.push({
    id: 'filter_sort_catalog',
    label: 'Sort by',
    value: 'sort',
    config: { parent: 'catalog', multiple: false },
    options: [
      { label: 'Discount - High to Low', value: 'discount:desc' },
      { label: 'Price - Low to High', value: 'price:asc' },
      { label: 'Price - High to Low', value: 'price:desc' }
    ]
  })
}
if (props.config.filter_sub_categories) {
  const subCategories = (props.data.sub_categories || []).map((k) => ({ label: k, value: k.toLowerCase() })); 
  filterItems.value.push({
    id: 'filter_sub_category',
    label: 'Sub Category',
    value: 'sub_categories',
    config: { parent: 'provider', multiple: true },
    options: subCategories
  });
}
if (props.config.filter_min_catalog_rating) {
  filterItems.value.push({
    id: 'filter_min_catalog_rating',
    label: 'Catalog Rating',
    value: 'min_catalog_rating',
    config: { parent: 'provider', multiple: false },
    options: [
      { label: '4 & Above', value: 4 },
      { label: '3 & Above', value: 3 },
      { label: '2 & Above', value: 2 },
      { label: '1 & Above', value: 1 }
    ]
  });
}
if (props.config.filter_price_range) {
  filterItems.value.push({
    id: 'filter_price_range',
    label: 'Price Range',
    value: 'price',
    config: { parent: 'catalog', multiple: true },
    options: [{ min: 0, max: 10000 }]
  });
}
if (props.config.filter_discount_percentage) {
  filterItems.value.push({
    id: 'filter_discount_percentage',
    label: 'Discount',
    value: 'min_discount_percentage',
    config: { parent: 'catalog', multiple: false },
    options: [
      { label: '50% & Above', value: 50 },
      { label: '30% & Above', value: 30 },
      { label: '10% & Above', value: 10 }
    ]
  });
}
if (props.config.filter_brands_health_and_wellness) {
  filterItems.value.push({
    id: 'filter_brands_health_and_wellness',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
	  { label: "Punh Nutrition", value: "Punh Nutrition" },
	  { label: "Nigam Silver Technologies", value: "Nigam Silver Technologies" },
	  { label: "Public Hospital", value: "Public Hospital" },
	  { label: "CIPZER HERBALS", value: "CIPZER HERBALS" },
	  { label: "Mother Sparsh", value: "Mother Sparsh" },
	  { label: "Dr.MOREPEN", value: "Dr.MOREPEN" },
	  { label: "Nua Woman", value: "Nua Woman" },
	  { label: "Naturali", value: "Naturali" },
	  { label: "FAST & UP", value: "FAST & UP" },
	  { label: "Bold Care", value: "Bold Care" },
	  { label: "Baby Organo", value: "Baby Organo" },
	  { label: "Indicharkha", value: "Indicharkha" },
	  { label: "CARPET OVERSEAS", value: "CARPET OVERSEAS" },
	  { label: "GETFAST PHARMACEUTICALS PRIVATE LIMITED", value: "GETFAST PHARMACEUTICALS PRIVATE LIMITED" }
     ]
  });
}
 
 
 
if (props.config.filter_brands_fashion) {
  filterItems.value.push({
    id: 'filter_brands_fashion',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
      	  { label: "GIVA", value: "GIVA" },
	  { label: "ASIAN FOOTWEARS PVT. LTD", value: "ASIAN FOOTWEARS PVT. LTD" },
	  { label: "ATRANGI LIFESTYLE PRIVATE LIMITED", value: "ATRANGI LIFESTYLE PRIVATE LIMITED" },
	  { label: "Kalyan Silks", value: "Kalyan Silks" },
	  { label: "KRI India", value: "KRI India" },
	  { label: "The Natural Wash", value: "The Natural Wash" },
	  { label: "DUDANI RETAIL PRIVATE LIMITED", value: "DUDANI RETAIL PRIVATE LIMITED" },
	  { label: "Palmonas", value: "Palmonas" },
	  { label: "DOLLAR INDUSTRIES LTD.", value: "DOLLAR INDUSTRIES LTD." },
	  { label: "NEEMANS PRIVATE LIMITED", value: "NEEMANS PRIVATE LIMITED" },
	  { label: "Fashion House", value: "Fashion House" },
	  { label: "Mrs Right", value: "Mrs Right" },
	  { label: "Croma", value: "Croma" },
	  { label: "KHADIM INDIA LTD", value: "KHADIM INDIA LTD" },
	  { label: "THREAD BUCKET STUDIO LLP", value: "THREAD BUCKET STUDIO LLP" },
	  { label: "LIBERTY SHOES LIMITED", value: "LIBERTY SHOES LIMITED" },
	  { label: "PARAGON POLYMER PRODUCTS PRIVATE LIMITED", value: "PARAGON POLYMER PRODUCTS PRIVATE LIMITED" },
	  { label: "CAMPUS ACTIVEWEAR LIMITED", value: "CAMPUS ACTIVEWEAR LIMITED" },
	  { label: "Vega Industries Private Limited", value: "Vega Industries Private Limited" },
	  { label: "Subha Exim International", value: "Subha Exim International" }
    ]
  });
}
 
 
if (props.config.filter_brands_electronics) {
  filterItems.value.push({
    id: 'filter_brands_electronics',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
	  { label: "Hapipola india LLP", value: "Hapipola india LLP" },
	  { label: "K.KANAGARAJ WATCH HOUSE", value: "K.KANAGARAJ WATCH HOUSE" },
	  { label: "Portronics Digital Pvt. Ltd.", value: "Portronics Digital Pvt. Ltd." },
	  { label: "Gem Computers", value: "Gem Computers" },
	  { label: "A ONE MOBILE", value: "A ONE MOBILE" },
	  { label: "CasaGold", value: "CasaGold" },
	  { label: "OASIS INDIA IT STORE PRIVATE LIMITED", value: "OASIS INDIA IT STORE PRIVATE LIMITED" },
	  { label: "Vivo", value: "Vivo" },
	  { label: "Karma Connections", value: "Karma Connections" },
	  { label: "The Spring Palette", value: "The Spring Palette" },
	  { label: "ValueActive", value: "ValueActive" },
	  { label: "Saryu Homes", value: "Saryu Homes" },
	  { label: "Boult Audio", value: "Boult Audio" },
	  { label: "ACWO LIFESTYLE PRIVATE LIMITED", value: "ACWO LIFESTYLE PRIVATE LIMITED" },
	  { label: "Hammer", value: "Hammer" },
	  { label: "Skullcandy", value: "Skullcandy" },
	  { label: "Pebbles Star Technologies", value: "Pebbles Star Technologies" },
	  { label: "The Write Stuff", value: "The Write Stuff" },
	  { label: "A.A. ASSOCIATES", value: "A.A. ASSOCIATES" },
	  { label: "TechBzar", value: "TechBzar" }
	]
  });
}
 
 
if (props.config.filter_brands_bpc) {
  filterItems.value.push({
    id: 'filter_brands_bpc',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
	    options: [
	  { label: "The Man Company", value: "The Man Company" },
	  { label: "Ecotrail Personal Care Private Limited", value: "Ecotrail Personal Care Private Limited" },
	  { label: "Wow Skin Science", value: "Wow Skin Science" },
	  { label: "Bombay Shaving Company", value: "Bombay Shaving Company" },
	  { label: "Sunscoop", value: "Sunscoop" },
	  { label: "UNIFORMLO SALES", value: "UNIFORMLO SALES" },
	  { label: "Revlon", value: "Revlon" },
	  { label: "Bare Anatomy", value: "Bare Anatomy" },
	  { label: "Chemist at play", value: "Chemist at play" },
	  { label: "Moraze Cosmetics", value: "Moraze Cosmetics" },
	  { label: "Ustraa Store", value: "Ustraa Store" },
	  { label: "SIDDHAYU HEALTHCARE PRIVATE LIMITED", value: "SIDDHAYU HEALTHCARE PRIVATE LIMITED" },
	  { label: "SWISS BEAUTY COSMETICS PRIVATE LIMITED", value: "SWISS BEAUTY COSMETICS PRIVATE LIMITED" },
	  { label: "The Moms Co", value: "The Moms Co" },
	  { label: "Love Earth", value: "Love Earth" },
	  { label: "COURTYARD RETAIL PRIVATE LIMITED", value: "COURTYARD RETAIL PRIVATE LIMITED" },
	  { label: "Adithi Enterprises", value: "Adithi Enterprises" },
	  { label: "Elitty Beauty", value: "Elitty Beauty" },
	  { label: "BELLAVITA", value: "BELLAVITA" },
	  { label: "Oxolloxo", value: "Oxolloxo" }
	]
  });
}
 
 
if (props.config.filter_brands_grocery) {
  filterItems.value.push({
    id: 'filter_brands_grocery',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
	  { label: "Cosmic Traders", value: "Cosmic Traders" },
	  { label: "BODY CUPID COSMETICS PRIVATE LIMITED", value: "BODY CUPID COSMETICS PRIVATE LIMITED" },
	  { label: "FutureLife Foods Private Limited", value: "FutureLife Foods Private Limited" },
	  { label: "Tribalight", value: "Tribalight" },
	  { label: "Khud Sadhan Raturi", value: "Khud Sadhan Raturi" },
	  { label: "Sunshine Electricals And Electronics", value: "Sunshine Electricals And Electronics" },
	  { label: "Mamaya Organics LLP", value: "Mamaya Organics LLP" },
	  { label: "IGNITED BRAIN SOLUTIONS LLP", value: "IGNITED BRAIN SOLUTIONS LLP" },
	  { label: "Kamadhei Agro Farm LLP", value: "Kamadhei Agro Farm LLP" },
	  { label: "FEMORA INDIA PRIVATE LIMITED", value: "FEMORA INDIA PRIVATE LIMITED" },
	  { label: "RABBIT STATIONERY PRIVATE LIMITED LTD", value: "RABBIT STATIONERY PRIVATE LIMITED LTD" },
	  { label: "KIT&CO", value: "KIT&CO" },
	  { label: "NAMMAHUT TAMILNADU", value: "NAMMAHUT TAMILNADU" },
	  { label: "the hatke", value: "the hatke" },
	  { label: "Swamini Enterprises", value: "Swamini Enterprises" },
	  { label: "Maha Sakthi Natural Healthy Foods Pvt Ltd", value: "Maha Sakthi Natural Healthy Foods Pvt Ltd" },
	  { label: "Shri Krishnam Enterprises", value: "Shri Krishnam Enterprises" },
	  { label: "SHANAYA - ONE STOP STORE FOR STATIONARY & HOME KITCHEN DECOR", value: "SHANAYA - ONE STOP STORE FOR STATIONARY & HOME KITCHEN DECOR" },
	  { label: "Rajdhani", value: "Rajdhani" },
	  { label: "Paper Planning And More", value: "Paper Planning And More" }
	]
  });
}
 
 
if (props.config.filter_brands_home_and_kitchen) {
  filterItems.value.push({
    id: 'filter_brands_home_and_kitchen',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
	  { label: "VI-JOHN HEALTHCARE INDIA LLP", value: "VI-JOHN HEALTHCARE INDIA LLP" },
	  { label: "Kuhi", value: "Kuhi" },
	  { label: "BANO", value: "BANO" },
	  { label: "The Wallet Store", value: "The Wallet Store" },
	  { label: "Laxmi Sales", value: "Laxmi Sales" },
	  { label: "The Good Luck", value: "The Good Luck" },
	  { label: "HAMILTON HOUSEWARES PVT LTD", value: "HAMILTON HOUSEWARES PVT LTD" },
	  { label: "R A CREATIONS", value: "R A CREATIONS" },
	  { label: "Fabindia Limited", value: "Fabindia Limited" },
	  { label: "Neelam Terracotta Group", value: "Neelam Terracotta Group" },
	  { label: "Ferns N Petals (FNP)", value: "Ferns N Petals (FNP)" },
	  { label: "UCOOK", value: "UCOOK" },
	  { label: "Re'equil. India Private Limited", value: "Re'equil. India Private Limited" },
	  { label: "CTM HOMESTYLE PRIVATE LIMITED", value: "CTM HOMESTYLE PRIVATE LIMITED" },
	  { label: "HAMILTON SCIENCES PRIVATE LIMITED", value: "HAMILTON SCIENCES PRIVATE LIMITED" },
	  { label: "BESTOR INDIA", value: "BESTOR INDIA" },
	  { label: "Soumik Mat Centre", value: "Soumik Mat Centre" },
	  { label: "Unity Works", value: "Unity Works" },
	  { label: "KIMTI", value: "KIMTI" },
	  { label: "FNP - Express Delivery", value: "FNP - Express Delivery" }
	]
  });
}
 
 
 
if (props.config.filter_brands_food_beverages) {
  filterItems.value.push({
    id: 'filter_brands_food_beverages',
    label: 'Brands',
    value: 'search_text',
    config: { parent: 'provider', multiple: false },
    options: [
	  { label: "Wow! Momo", value: "Wow! Momo" },
	  { label: "The Pizza Home", value: "The Pizza Home" },
	  { label: "Taco Bell", value: "Taco Bell" },
	  { label: "Cafe Coffee Day", value: "Cafe Coffee Day" },
	  { label: "Nidhi Foods", value: "Nidhi Foods" },
	  { label: "Nomad Pizza - Travellers Series", value: "Nomad Pizza - Travellers Series" },
	  { label: "The Momo Co.", value: "The Momo Co." },
	  { label: "Chinese Wok", value: "Chinese Wok" },
	  { label: "Raju Pav Bhaji Counter", value: "Raju Pav Bhaji Counter" },
	  { label: "Pizza Expert", value: "Pizza Expert" },
	  { label: "The Season Cafe", value: "The Season Cafe" },
	  { label: "Itminaan Matka Biryani - Slow Cooked", value: "Itminaan Matka Biryani - Slow Cooked" },
	  { label: "McDonald's", value: "McDonald's" }
	]
  });
}
const attributes = props.data.attributes || [];
attributes.forEach((item) => {
  filterItems.value.push({
    id: `filter_${item.key}`,
    label: item.label,
    value: item.key,
    config: { parent: 'catalog', multiple: true },
    options: Array.isArray(item.value)
      ? item.value.map((k) => ({ label: k, value: k.toLowerCase() }))
      : []
  });
});
const filterBoxItems = computed(() => filterItems.value.filter((k) => k.options.length > 0));

const mergeFilters = (data = {}) => {
  const providerFilterSelected = {};
  const catalogFilterSelected = {};
  Object.entries(data).forEach(([item, value]) => {
    const parent = filterItems.value.find(k => k.value === item).config.parent;
    if (parent === 'provider') providerFilterSelected[item] = value;
    if (parent === 'catalog') catalogFilterSelected[item] = value;
  });
  providerFilters.value = { ...providerFilters.value, ...providerFilterSelected };
  catalogFilters.value = { ...catalogFilters.value, ...catalogFilterSelected };
  closeFilterBox();
  //handleFilterBoxClosed(); // Close the filter box and remove history state
  applyFilters();
};


const applyFilters = () => {
  trackGoogleAnalystics(providerFilters, catalogFilters);
  emit('apply', providerFilters.value, catalogFilters.value);
};

function filterNonEmpty(obj) {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      result[key] = value;
    }
  }
  return result;
}

const trackGoogleAnalystics = (providerFilters, catalogFilters) => {
  // Map provider filters to GA payload keys
  const providerPayload = filterNonEmpty({
    provider_categories: providerFilters.value.categories?.join(','),
    provider_sub_categories: providerFilters.value.sub_categories?.join(','),
    provider_seller_count: providerFilters.value.seller_np_ids?.length,
    provider_min_rating: providerFilters.value.min_catalog_rating,
    provider_is_open_now: providerFilters.value.is_open_now,
    provider_is_deliverable: providerFilters.value.is_deliverable,
    provider_food_type: providerFilters.value.food_type,
    provider_sort: providerFilters.value.sort,
  });

  // Map catalog filters to GA payload keys
  const catalogPayload = filterNonEmpty({
    catalog_price_ranges: catalogFilters.value.price?.join(','),
    catalog_min_discount: catalogFilters.value.min_discount_percentage,
    catalog_food_type: catalogFilters.value.food_type,
    catalog_sort: catalogFilters.value.sort,
  });

  // Merge payloads
  const payload = {
    [EventParameter.CTA_NAME]: 'apply_filters',
    ...providerPayload,
    ...catalogPayload,
  };

  GoogleAnalytics.cta(payload);

};


//const closeFilterBox = () => isFilterBoxOpened.value = false;

//const isFilterBoxOpened = ref(false);
let hasPushedState = false;
let ignoreNextPopState = false;

watch(isFilterBoxOpened, (newVal) => {
  if (newVal) {
    history.pushState({ popup: true }, '');
    hasPushedState = true;
  }
});

const handlePopState = () => {
  if (ignoreNextPopState) {
    ignoreNextPopState = false;
    return; // skip this popstate caused by manual close
  }

  if (isFilterBoxOpened.value) {
    isFilterBoxOpened.value = false;
    hasPushedState = false;
  }
};

const closeFilterBox = () => {
  if (isFilterBoxOpened.value) {
    isFilterBoxOpened.value = false;

    if (hasPushedState) {
      hasPushedState = false;
      ignoreNextPopState = true; // prevent the popstate that follows from triggering close again
      history.back();
    }
  }
};

onMounted(() => {
  window.addEventListener('popstate', handlePopState);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState);
});

</script>
